/**
 * Decide which ship-to address should be selected for a customer, given the
 * addresses that customer actually owns.
 *
 * WHY THIS EXISTS — the bug it prevents (Jesse, 2026-08-07):
 * Switching the customer on a quote left the PREVIOUS customer's address_id in
 * React state. A controlled `<select>` whose `value` matches none of its
 * `<option>`s does not render blank: the browser's selectedness-reset algorithm
 * re-selects the FIRST option. So the screen showed a plausible address for the
 * new customer while state still held a foreign id, and the save was rejected
 * by jobq-api with:
 *
 *   shipping_address_id 24 is not a valid active address for this customer.
 *
 * (Address 24 belongs to First Edition; the quote was Faith Technologies'.)
 *
 * The rule this encodes: never keep a selected address that the currently
 * selected customer does not own.
 *
 * @param {Array<{address_id: number, is_default?: boolean}>} list
 *        The customer's ACTIVE addresses, as returned by
 *        `GET /customers/{id}/addresses`.
 * @param {object}  [opts]
 * @param {number|string|null} [opts.storedQuoteAddressId]
 *        `shipping_address_id` saved on the quote/SO being edited, if any.
 *        Wins when this customer still owns it — an existing record keeps the
 *        address it was written with.
 * @param {number|string} [opts.currentPick]
 *        What the operator currently has selected. Kept only when owned.
 * @returns {string} the address_id to select, as a string ('' when the customer
 *          has no addresses — the API then falls back to the customer default).
 */
export default function resolveShippingAddressId(list, opts = {}) {
    const { storedQuoteAddressId = null, currentPick = '' } = opts;
    const addresses = Array.isArray(list) ? list : [];

    const ownedByCustomer = (id) =>
        id !== null && id !== undefined && id !== '' &&
        addresses.some(a => String(a.address_id) === String(id));

    // Edit mode: preserve what the record was saved with, but only while this
    // customer still owns it. A since-deactivated address is absent from the
    // list and the API refuses it on save too, so falling through to the
    // default is what keeps the record saveable at all.
    if (ownedByCustomer(storedQuoteAddressId)) return String(storedQuoteAddressId);

    // An operator's explicit pick survives — unless it belongs to someone else.
    if (ownedByCustomer(currentPick)) return String(currentPick);

    const def = addresses.find(a => a.is_default) || addresses[0];
    return def ? String(def.address_id) : '';
}
